<template>
	<div>
		<div class="cashbook-control">
			<div :class="{ 'cashbook-info': true, 'cashbook-control-col': true, 'full-width': !showChiefCashierBlock }">
				<div class="cashbook-info-col">
					<div class="flex">
						<h3 class="gray-50">Касова Книга {{ !isCashbookClosed ? `№${cashbook.number}` : '' }}</h3>
						<h5 v-if="showUnfinishedInventorizationWarn">необхідно виправити опис наявних ТМЦ</h5>
					</div>
					<div class="cashbook-date-and-count">
						<span v-if="isCashbookClosed"> не відкрита </span>
						<span v-else-if="!cashbook.is_today" class="red"> не закрита за {{ cashbookDate }} </span>
						<span v-else-if="isCashbookClosing" class="yellow"> зачиняється за {{ cashbookDate }} </span>
						<div v-else class="labeled-icon cashbook-date">
							<i class="icon-start" />
							<span class="gray-50">{{ cashbookDate }}</span>
						</div>
						<div v-if="showCashdesksCount" class="cashdesk-shifts labeled-icon red">
							<i class="icon-error" />
							<span class="cashdesk-count">{{ openedCashdesksText }}</span>
						</div>
					</div>
				</div>
				<div class="cashbook-info-col cashbook-actions-col">
					<div v-if="isCashbookClosed || isChiefCashierCurrentUser" class="cashbook-actions">
						<NP-Button
							v-if="isCashbookClosed"
							type="button"
							:class="managerModalConfig.class"
							:loading="managerModalConfig.loading"
							@click.stop="managerModalConfig.action"
						>
							{{ managerModalConfig.text }}
						</NP-Button>
						<NP-Button
							v-if="canTransferShift"
							type="button"
							:loading="
								loadingActions.includes('initCashbookShiftTransfer') || loadingActions.includes('getIssueAmount')
							"
							class="transfer-shift-btn"
							@click="onClickTransferShift"
						>
							Передати зміну СК
						</NP-Button>
						<NP-Button
							v-else-if="canCancelTransfer"
							type="button"
							class="cancel-transfer-shift-btn"
							:loading="loadingActions.includes('cancelCashbookShiftTransfer')"
							@click="() => cancelCashbookShiftTransfer({ loadingAction: true })"
						>
							Відмінити передачу зміни СК
						</NP-Button>
						<NP-Button
							v-else-if="canCloseShift"
							type="button"
							class="close-cashbook-btn"
							@click.stop="
								() => toggleInventoryActModal({ scenario: scenarios.closeShift, cash: cashbook.closing_cash })
							"
						>
							Закрити зміну СК
						</NP-Button>
						<NP-Button
							:disabled="!!openedCashdesks.length"
							v-if="(isCashbookOpened || isCashbookClosing) && isChiefCashierCurrentUser"
							type="button"
							class="close-cashbook-btn"
							@click="
								isCashbookClosing
									? confirmCashbookClosal({ loadingAction: true })
									: toggleCashbookClosalModal({ loadingAction: true })
							"
							:loading="isClosalButtonLoading"
						>
							Закрити касову книгу
						</NP-Button>
						<NP-Button
							v-if="isCashbookClosing && isChiefCashierCurrentUser"
							type="button"
							@click="() => cancelCashbookClosal({ loadingAction: true })"
							:loading="isCancelClosalButtonLoading"
						>
							Відмінити закриття касової книги
						</NP-Button>
					</div>
				</div>
			</div>
			<div v-if="showChiefCashierBlock" class="chief-cashier cashbook-control-col">
				<div class="chief-cashier-name">
					<h3 :title="chiefCashier.name">
						{{ chiefCashier.name }}
					</h3>
					<span>Старший касир</span>
				</div>
				<NP-Button
					v-if="canAcceptShift"
					type="button"
					class="confrm-transfer-shift-btn"
					:loading="
						loadingActions.includes('confirmCashbookShiftTransfer') || loadingActions.includes('getIssueAmount')
					"
					@click.stop="onClickAcceptShift"
				>
					Прийняти зміну СК
				</NP-Button>
			</div>
		</div>
		<CashbookClosalModal
			v-if="state.render && state.render === 'cashbook_closal_modal'"
			:state="state"
			:toggle="toggleCashbookClosalModal"
			:toggle-inventory-act-modal="toggleInventoryActModal"
			:set-cashbook-closal-data="setCashbookClosalData"
			:errors="validationErrors"
			:loading-actions="loadingActions"
			:lookup-users="lookupUsers"
			:root="root"
		/>
		<InventoryActModal
			v-if="state.render === 'inventory_act'"
			:toggle="toggleInventoryActModal"
			:submit="inventoryActModalSubmit"
			:loading="inventoryActLoading"
			:errors="validationErrors"
			:lookup-users="lookupUsers"
			:root="root"
			:user-from-id="actUserFromId"
			:user-from-name="actUserFromName"
			:user-from-position="actUserFromPosition"
			:user-to-id="actUserToId"
			:user-to-name="actUserToName"
			:user-to-position="actUserToPosition"
			:cash="actCash"
			:act-type="actType"
			:recorded-amount="actRecordedAmount"
			:current-scenario="actScenario"
		/>
		<AlertModal ref="alertModal" />
		<WorkplaceSelectModal
			v-if="state.render && state.render === 'select_workplace_modal'"
			:state="state"
			:toggle="toggleWorkplaceSelectModal"
			:loading-actions="loadingActions"
			:open-cashbook="openCashbookWithShift"
		/>
	</div>
</template>

<script>
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';
import intersection from 'lodash/intersection';

import CashbookClosalModal from './cashbook-closal-modal.vue';
import WorkplaceSelectModal from './workplace-select-modal.vue';
import AlertModal from './alert-modal.vue';
import scenarios from '@/components/inventory-act-modal/scenarios';
import { enum as inventoryAssetStatuses } from '@repo/enums/inventory-record-asset-statuses';

export default {
	name: 'CashbookControl',
	components: { CashbookClosalModal, AlertModal, WorkplaceSelectModal },
	props: {
		actType: { type: String, default: null },
		actUserFromId: { type: Number, default: null },
		actUserFromName: { type: String, default: null },
		actUserFromPosition: { type: String, default: null },
		actUserToId: { type: Number, default: null },
		actUserToName: { type: String, default: null },
		actUserToPosition: { type: String, default: null },
		actScenario: { type: String, default: null },
		actRecordedAmount: { type: [String, Number], default: null },
		alertMessage: { type: String, default: null },
		state: { type: [String, Object], default: 'loading' },
		actCash: { type: Object, default: null },
		root: { type: Object, default: () => ({}) },
		cashbook: { type: Object, default: () => ({}) },
		cashbookShift: { type: Object, default: () => ({}) },
		openedCashdesks: { type: Array, default: () => [] },
		loadingActions: { type: Array, default: () => [] },
		validationErrors: { type: Array, default: () => [] },
		openCashbookOpenModal: { type: Function, default: null },
		toggleWorkplaceSelectModal: { type: Function, default: null },
		openCashbookWithShift: { type: Function, default: null },
		openCashbook: { type: Function, default: null },
		initCashbookClosal: { type: Function, default: null },
		cancelCashbookClosal: { type: Function, default: null },
		confirmCashbookClosal: { type: Function, default: null },
		setCashbookClosalData: { type: Function, default: null },
		initCashbookShiftTransfer: { type: Function, default: null },
		confirmCashbookShiftTransfer: { type: Function, default: null },
		cancelCashbookShiftTransfer: { type: Function, default: null },
		toggleInventoryActModal: { type: Function, default: null },
		toggleCashbookClosalModal: { type: Function, default: null },
		inventoryActModalSubmit: { type: Function, default: null },
		lookupUsers: { type: Function, default: null },
		getIssueAmount: { type: Function, default: null },
		issueAmount: { type: Number, default: 0 }
	},
	data() {
		return {
			scenarios
		};
	},
	computed: {
		...mapGetters({
			isCashbookOpened: 'root/isCashbookOpened',
			isCashbookClosed: 'root/isCashbookClosed',
			isCashbookClosing: 'root/isCashbookClosing',
			isCashbookShiftClosed: 'root/isCashbookShiftClosed',
			isChiefCashierCurrentUser: 'root/isChiefCashier'
		}),
		showChiefCashierBlock() {
			return !this.isCashbookShiftClosed && this.chiefCashier && !this.isChiefCashierCurrentUser;
		},
		chiefCashier() {
			return (
				!this.isCashbookShiftClosed && {
					id: this.cashbookShift.user_id,
					name: `${this.cashbookShift.last_name} ${this.cashbookShift.first_name} ${
						this.cashbookShift.patronymic || ''
					}`
				}
			);
		},
		cashbookDate() {
			return this.cashbook ? DateTime.fromISO(this.cashbook.created_at).toFormat('dd/MM/yyyy') : '';
		},
		isClosalButtonLoading() {
			let actions = intersection(this.loadingActions, [
				'initCashbookClosal',
				'toggleCashbookClosalModal',
				'confirmCashbookClosal'
			]);
			return actions && !!actions.length;
		},
		isCancelClosalButtonLoading() {
			let actions = intersection(this.loadingActions, ['cancelCashbookClosal', 'toggleCashbookClosalModal']);
			return actions && !!actions.length;
		},
		showCashdesksCount() {
			return this.isChiefCashierCurrentUser && this.openedCashdesks.length;
		},
		openedCashdesksText() {
			return this.openedCashdesks.length ? `Відкрито ДК - ${this.openedCashdesks.length}` : '';
		},
		managerModalConfig() {
			if (!this.isChiefCashierCurrentUser || this.isCashbookShiftClosed) {
				return {
					action: () => this.openCashbookOpenModal(),
					text: 'Відкрити КК та зміну СК',
					loading: this.loadingActions.includes('openCashbookWithShift'),
					class: 'open-cashbook-and-shift-btn'
				};
			}
			return {
				action: () => this.openCashbook({ loadingAction: true }),
				text: 'Відкрити касову книгу',
				loading: this.loadingActions.includes('openCashbook') || this.loadingActions.includes('openCashbookWithShift'),
				class: 'open-cashbook-btn'
			};
		},
		canTransferShift() {
			return (
				!this.isCashbookShiftClosed &&
				!this.isCashbookClosed &&
				!this.isCashbookClosing &&
				this.isChiefCashierCurrentUser &&
				!this.cashbookShift.is_transferring
			);
		},
		canAcceptShift() {
			return (
				!this.isCashbookShiftClosed &&
				!this.isCashbookClosed &&
				!this.isCashbookClosing &&
				this.cashbookShift.is_transferring &&
				!this.isChiefCashierCurrentUser
			);
		},
		canCancelTransfer() {
			return (
				!this.isCashbookShiftClosed &&
				!this.isCashbookClosed &&
				!this.isCashbookClosing &&
				this.cashbookShift.is_transferring &&
				this.isChiefCashierCurrentUser
			);
		},
		canCloseShift() {
			return !this.isCashbookShiftClosed && this.isCashbookClosed && this.isChiefCashierCurrentUser;
		},
		inventoryActLoading() {
			return (
				this.loadingActions.includes('inventoryActModalSubmit') ||
				this.loadingActions.includes('cancelCashbookShiftTransfer')
			);
		},
		showUnfinishedInventorizationWarn() {
			return (
				this.isChiefCashierCurrentUser &&
				this.root.inventory_record?.asset_status === inventoryAssetStatuses.revisionByResponsible
			);
		}
	},
	methods: {
		async checkIssueAmount() {
			if (this.root?.salepoint?.branch !== 'Філія № 4 (Львівська обл.)') {
				return;
			}

			await this.getIssueAmount({ loadingAction: true });

			if (this.issueAmount > 0) {
				await this.$refs.alertModal.show(this.issueAmount);
			}
		},
		async onClickTransferShift() {
			await this.checkIssueAmount();

			return this.initCashbookShiftTransfer({ loadingAction: true });
		},
		async onClickAcceptShift() {
			await this.checkIssueAmount();

			setTimeout(() => {
				this.toggleInventoryActModal({ scenario: this.scenarios.confirmShiftTransfer });
			}, 0);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@assets/scss/variables.scss';
@import '@assets/scss/mixins.scss';
@import '@assets/scss/shared.scss';

$icon-start: '~@assets/images/icon-start.svg';
$icon-error: '~@assets/images/icon-error.svg';

h3 {
	font-family: Proxima Nova Semibold, sans-serif;
}
.icon-start {
	@include make-icon($icon-start, $gray-30, 18px, 18px);
}
.icon-error {
	@include make-icon($icon-error, $red, 18px, 18px);
}

.cashbook-control {
	display: flex;
	flex-wrap: nowrap;
	width: 100%;
	justify-content: space-between;
	.cashbook-control-col:not(:last-child) {
		margin-right: 24px;
	}
	.cashbook-control-col:nth-last-child(1) {
		flex-basis: calc(50% - 12px);
	}
	.cashbook-control-col {
		min-height: 88px;
		flex-basis: 50%;
		background-color: $white;
		border-radius: $border-radius;
		&.full-width {
			flex-basis: 100%;
		}
	}
	.cashbook-info {
		display: flex;
		padding: 20px 24px;
		background-color: white;
		& .cashbook-info-col {
			.flex {
				display: flex;
				align-items: center;
				margin-bottom: 7px;
				* {
					margin-right: 10px;
				}
				h5 {
					color: $red;
				}
			}
			h3 {
				display: flex;
				align-items: center;
			}
			span {
				font-size: 14px;
			}
		}
		& .cashbook-actions-col {
			display: flex;
			flex: 1;
			flex-direction: row;
			justify-content: flex-end;
			align-items: center;
			.cashbook-actions {
				display: flex;
				justify-content: flex-end;
				.button {
					margin-left: 8px;
				}
			}
		}
		.cashbook-date-and-count {
			display: flex;
			flex-direction: row;
			flex-wrap: wrap;
			& > :not(:last-child) {
				margin-right: 10px;
			}
			& .cashdesk-shifts,
			& .cashbook-date {
				display: flex;
				font-size: 14px;
			}
			.cashdesk-count {
				font-size: 12px;
				font-style: italic;
			}
		}
	}
	.chief-cashier {
		align-items: center;
		color: $gray-50;
		display: flex;
		flex-grow: 0;
		justify-content: space-between;
		padding: 20px;
		.chief-cashier-name {
			h3 {
				margin-bottom: 4px;
			}
			span {
				font-size: 14px;
			}
		}
		& div:first-child {
			flex: auto;
			overflow: hidden;
			& h3 {
				flex: 0;
				min-width: 0;
				overflow: hidden;
				white-space: nowrap;
				text-overflow: ellipsis;
			}
		}
		& .button {
			flex-grow: 0;
			flex-shrink: 0;
			margin-left: 8px;
		}
	}
	@media (max-width: 1318px) {
		flex-wrap: wrap;
		.cashbook-control-col {
			width: 100%;
			flex-basis: 100% !important;
		}
		.cashbook-control-col:not(:last-child) {
			margin-right: 0;
			margin-bottom: 20px;
		}
		.cashbook-info {
			.cashbook-info-col:last-child {
				flex: 2;
			}
		}
	}
	@include mobile() {
		.cashbook-control-col {
			flex-basis: 100% !important;
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			padding: 16px;
			&.cashbook-info {
				margin-bottom: 20px;
			}
			.chief-cashier-name {
				margin-bottom: 16px;
				width: 100%;
			}
			.cashbook-info-col {
				margin-bottom: 16px;
				flex: 1;
			}
			.cashbook-actions-col {
				flex: 1 !important;
				* {
					flex: 1 !important;
					margin-left: 0 !important;
				}
			}
			.button {
				margin-left: 0;
			}
		}
	}
}
</style>
